import {useId, type KeyboardEvent} from 'react';
import {Search, ImagePlus, ListVideo, Tv, User, LayoutGrid, Check, Globe} from 'lucide-react';
import {
  Checkbox,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
  Textarea,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {ImageUploadField} from './ImageUploadField';
import {CountrySelect} from '@/components/shared/CountrySelect';
import {LanguageSelect} from '@/components/shared/LanguageSelect';
import {useClassificationConfig} from '@/api/hooks';
import type {JobRecency} from '@/api/jobs';
import {PLATFORM_ID} from '@/lib/platform';
import {isPolicyViolationType, isVisibleForFacet} from '@/lib/classificationType';

export type SearchType =
  | 'query'
  | 'image'
  | 'google'
  | 'playlist'
  | 'channel'
  | 'pinterest_profile'
  | 'pinterest_board'
  | 'pinterest_search';

export type Tab = {value: SearchType; label: string; icon: typeof Search};

// Which tabs exist is build-time (one deployment only ever targets one platform's spiders), so
// this is a constant, not state. query/image are rendered as special cases below (query has an
// extra Videos/Channels toggle, image renders ImageUploadField); every other tab is a single text
// input driven by TEXT_TAB_CONFIG.
export const TABS: readonly Tab[] =
  PLATFORM_ID === 'pinterest'
    ? [
        {value: 'pinterest_profile', label: 'Profile', icon: User},
        {value: 'pinterest_search', label: 'Search', icon: Search},
        {value: 'image', label: 'Image', icon: ImagePlus},
        {value: 'google', label: 'Web search', icon: Globe},
        {value: 'pinterest_board', label: 'Board', icon: LayoutGrid}
      ]
    : [
        {value: 'query', label: 'Text', icon: Search},
        {value: 'image', label: 'Image', icon: ImagePlus},
        {value: 'google', label: 'Web search', icon: Globe},
        {value: 'playlist', label: 'Playlist', icon: ListVideo},
        {value: 'channel', label: 'Channel', icon: Tv}
      ];

type TextTabValue = Exclude<SearchType, 'query' | 'image'>;

/**
 The search engines a web search can add to Google - GoogleSearchClient.Engines. On Pinterest only
 Yandex also finds pins; the others would bill pages for nothing.
 */
export const EXTRA_ENGINES: ReadonlyArray<{value: string; label: string}> =
  PLATFORM_ID === 'pinterest'
    ? [{value: 'yandex', label: 'Yandex'}]
    : [
        {value: 'naver', label: 'Naver'},
        {value: 'yahoo', label: 'Yahoo'},
        {value: 'bing', label: 'Bing'},
        {value: 'yandex', label: 'Yandex'}
      ];

const TEXT_TAB_CONFIG: Record<TextTabValue, {fieldLabel: string; placeholder: string}> = {
  google: {
    fieldLabel: 'Web search',
    placeholder: 'e.g. exactly what you would type into google.com'
  },
  playlist: {
    fieldLabel: 'Playlist',
    placeholder: 'e.g. https://youtube.com/playlist?list=... or a playlist ID'
  },
  channel: {
    fieldLabel: 'Channel',
    placeholder: 'e.g. a channel URL, @handle, or channel ID'
  },
  pinterest_search: {
    fieldLabel: 'Search',
    placeholder: 'e.g. keywords to search Pinterest for'
  },
  pinterest_profile: {
    fieldLabel: 'Profile',
    placeholder: 'e.g. https://pinterest.com/someuser/ or a username'
  },
  pinterest_board: {
    fieldLabel: 'Board',
    placeholder: 'e.g. https://pinterest.com/someuser/some-board/ or someuser/some-board'
  }
};

export type InvestigationFormValue = {
  type: SearchType;
  /** 'both' is only offered when creating an investigation: it runs a video and a channel job. */
  resultKind: 'video' | 'channel' | 'both';
  /** How far back a text query looks for uploads. Only sent for the 'query' and 'google' tabs. */
  recency: JobRecency;
  searchTerm: string;
  textValue: string;
  image: File | null;
  collectFollowers: boolean;
  /** Falcon: also crawl each follower's own followers - two hops, no deeper. */
  collectFollowersOfFollowers: boolean;
  /**
    Pinterest profile: also collect the profile's own pins / its pins board by board. Mutually
   exclusive with collectFollowers and each other - one strategy per job.
   */
  collectPins: boolean;
  collectBoards: boolean;
  /** Pinterest search: which kind of result the query returns. */
  searchKind: 'profile' | 'pin';
  intent: string;
  language: string;
  /** Google tab: ISO 3166-1 alpha-2 country to search from, or '' for none. */
  country: string;
  /** Google tab: engines searched besides Google (EXTRA_ENGINES values). */
  extraEngines: string[];
  targetPolicyViolations: string[];
};

export const EMPTY_FORM_VALUE: InvestigationFormValue = {
  type: TABS[0]!.value,
  resultKind: 'video',
  recency: 'year',
  searchTerm: '',
  textValue: '',
  image: null,
  collectFollowers: false,
  collectFollowersOfFollowers: false,
  collectPins: false,
  collectBoards: false,
  searchKind: 'profile',
  intent: '',
  language: '',
  country: '',
  extraEngines: [],
  targetPolicyViolations: []
};

/**
 The strategy a JobsController create endpoint would use for this form value. NewInvestigationDialog
 dispatches `image` and `google` jobs through their own mutations, never through this.
 */
export function strategyForValue(value: InvestigationFormValue): string {
  switch (value.type) {
    case 'query': {
      return value.resultKind === 'channel' ? 'youtube_channel_search' : 'youtube_search';
    }

    case 'playlist': {
      return 'youtube_playlist';
    }

    case 'channel': {
      return 'youtube_channel';
    }

    case 'pinterest_profile': {
      if (value.collectFollowersOfFollowers) {
        return 'pinterest_profile_followers_of_followers';
      }

      if (value.collectFollowers) {
        // Pins ride along with followers as a flag on the job rather than a strategy of their own.
        return 'pinterest_profile_followers';
      }

      if (value.collectBoards) {
        return 'pinterest_profile_boards';
      }

      return value.collectPins ? 'pinterest_profile_pins' : 'pinterest_profile';
    }

    case 'pinterest_board': {
      return 'pinterest_board';
    }

    case 'pinterest_search': {
      return value.searchKind === 'pin' ? 'pinterest_pin_search' : 'pinterest_search';
    }

    case 'image': {
      return 'image_search';
    }

    case 'google': {
      return 'google_search';
    }
  }
}

// What each stored Pinterest profile strategy means for the "also collect" options, so
// valueForStrategy always sets all of them (a stale flag from the form's previous state could
// otherwise change which strategy a duplicated investigation round-trips to).
const profileValue = (
  textValue: string,
  collect: Partial<
    Pick<
      InvestigationFormValue,
      'collectFollowers' | 'collectFollowersOfFollowers' | 'collectPins' | 'collectBoards'
    >
  >
): Partial<InvestigationFormValue> => ({
  type: 'pinterest_profile',
  textValue,
  collectFollowers: false,
  collectFollowersOfFollowers: false,
  collectPins: false,
  collectBoards: false,
  ...collect
});
const PROFILE_VALUES = {
  pinterest_profile: (term: string) => profileValue(term, {}),
  pinterest_profile_followers: (term: string, collectPins = false) =>
    profileValue(term, {collectFollowers: true, collectPins}),
  // Followers of followers is a refinement of collecting followers, so it implies it.
  pinterest_profile_followers_of_followers: (term: string, collectPins = false) =>
    profileValue(term, {
      collectFollowers: true,
      collectFollowersOfFollowers: true,
      collectPins
    }),
  pinterest_profile_pins: (term: string) => profileValue(term, {collectPins: true}),
  pinterest_profile_boards: (term: string) => profileValue(term, {collectBoards: true})
};

/**
 Inverse of strategyForValue, for formValueFor: maps a stored strategy + its (already-normalized)
 search_term back to the tab/input that produced it.
 */
export function valueForStrategy(
  strategy: string,
  searchTerm: string,
  // The stored job's collect_pins flag - only a followers strategy carries it.
  collectPins = false
): Partial<InvestigationFormValue> {
  switch (strategy) {
    case 'youtube_search': {
      return {type: 'query', resultKind: 'video', searchTerm};
    }

    case 'youtube_channel_search': {
      return {type: 'query', resultKind: 'channel', searchTerm};
    }

    case 'youtube_playlist': {
      return {type: 'playlist', textValue: searchTerm};
    }

    case 'youtube_channel': {
      return {type: 'channel', textValue: searchTerm};
    }

    case 'pinterest_profile': {
      return PROFILE_VALUES.pinterest_profile(searchTerm);
    }

    case 'pinterest_profile_followers': {
      return PROFILE_VALUES.pinterest_profile_followers(searchTerm, collectPins);
    }

    case 'pinterest_profile_followers_of_followers': {
      return PROFILE_VALUES.pinterest_profile_followers_of_followers(searchTerm, collectPins);
    }

    case 'pinterest_profile_pins': {
      return PROFILE_VALUES.pinterest_profile_pins(searchTerm);
    }

    case 'pinterest_profile_boards': {
      return PROFILE_VALUES.pinterest_profile_boards(searchTerm);
    }

    case 'pinterest_board': {
      return {type: 'pinterest_board', textValue: searchTerm};
    }

    case 'pinterest_search': {
      return {type: 'pinterest_search', textValue: searchTerm, searchKind: 'profile'};
    }

    case 'pinterest_pin_search': {
      return {type: 'pinterest_search', textValue: searchTerm, searchKind: 'pin'};
    }

    case 'google_search': {
      return {type: 'google', textValue: searchTerm};
    }

    default: {
      return {type: 'query', resultKind: 'video', searchTerm};
    }
  }
}

/** What a stored investigation needs to re-open the form it was made from. */
export type InvestigationFormSource = {
  readonly strategy: string;
  readonly search_term: string;
  readonly collect_pins: boolean;
  readonly recency: JobRecency;
  readonly intent: string | null;
  readonly language: string | null;
  readonly search_country?: string | null | undefined;
  readonly search_engines?: readonly string[] | null | undefined;
  readonly target_policy_violations: readonly string[];
};

/**
 The whole form for an existing investigation: what its Duplicate action starts from.
 */
export function formValueFor(source: InvestigationFormSource): InvestigationFormValue {
  return {
    ...EMPTY_FORM_VALUE,
    ...valueForStrategy(source.strategy, source.search_term, source.collect_pins),
    recency: source.recency,
    intent: source.intent ?? '',
    language: source.language ?? '',
    country: source.search_country ?? '',
    extraEngines: (source.search_engines ?? []).filter((engine) => engine !== 'google'),
    targetPolicyViolations: [...source.target_policy_violations]
  };
}

/**
 The canSubmit rule every consumer applies: a policy violation picked, plus whichever input the
 active tab needs.
 */
export function isComplete(value: InvestigationFormValue): boolean {
  return (
    value.targetPolicyViolations.length > 0 &&
    (value.type === 'query'
      ? Boolean(value.searchTerm)
      : value.type === 'image'
        ? Boolean(value.image)
        : Boolean(value.textValue))
  );
}

type Props = {
  readonly value: InvestigationFormValue;
  readonly onChange: (next: InvestigationFormValue) => void;
  /** Enter key in a text input submits the form - callers wire this to their own submit handler. */
  readonly onSubmit?: () => void;
};

const RECENCY_OPTIONS: ReadonlyArray<{value: JobRecency; label: string}> = [
  {value: 'week', label: 'Past week'},
  {value: 'month', label: 'Past month'},
  {value: 'year', label: 'Past year'},
  {value: 'forever', label: 'Any time'}
];

export function InvestigationFormFields({value, onChange, onSubmit}: Props) {
  const setProfileCollect = (
    next: Partial<
      Pick<
        InvestigationFormValue,
        'collectFollowers' | 'collectFollowersOfFollowers' | 'collectPins' | 'collectBoards'
      >
    >
  ) => {
    onChange({...value, ...next});
  };

  const handleEnter = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && onSubmit) {
      onSubmit();
    }
  };

  // Base UI's single-select ToggleGroup reports [] when the pressed item is clicked again; every
  // segmented control here always has a value, so a deselect is simply ignored.
  const pick = <T extends string>(next: unknown[], allowed: readonly T[]): T | undefined =>
    allowed.find((option) => option === next[0]);

  const recencyField = (
    <Field orientation="horizontal">
      <FieldLabel>Uploaded</FieldLabel>
      <ToggleGroup
        variant="outline"
        spacing={0}
        size="sm"
        aria-label="Uploaded"
        value={[value.recency]}
        onValueChange={(next) => {
          const recency = pick(
            next,
            RECENCY_OPTIONS.map((option) => option.value)
          );
          if (recency) {
            onChange({...value, recency});
          }
        }}>
        {RECENCY_OPTIONS.map(({value: recency, label}) => (
          <ToggleGroupItem key={recency} value={recency}>
            {label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </Field>
  );

  return (
    <>
      <ToggleGroup
        variant="outline"
        spacing={0}
        size="sm"
        aria-label="Investigation type"
        className="w-full"
        value={[value.type]}
        onValueChange={(next) => {
          const type = pick(
            next,
            TABS.map((tab) => tab.value)
          );
          if (type) {
            onChange({...value, type});
          }
        }}>
        {TABS.map(({value: tabValue, label, icon: Icon}) => (
          <ToggleGroupItem key={tabValue} value={tabValue} className="flex-1">
            <Icon />
            {label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {value.type === 'query' ? (
        <FieldGroup className="gap-3">
          <Field>
            <FieldLabel htmlFor="investigation-search-term">Search query</FieldLabel>
            <Input
              autoFocus
              id="investigation-search-term"
              placeholder="e.g. climate change misinformation"
              value={value.searchTerm}
              onChange={(e) => {
                onChange({...value, searchTerm: e.target.value});
              }}
              onKeyDown={handleEnter}
            />
          </Field>
          <Field orientation="horizontal">
            <FieldLabel>Results</FieldLabel>
            <ToggleGroup
              variant="outline"
              spacing={0}
              size="sm"
              aria-label="Results"
              value={[value.resultKind]}
              onValueChange={(next) => {
                const resultKind = pick(next, ['video', 'channel', 'both'] as const);
                if (resultKind) {
                  onChange({...value, resultKind});
                }
              }}>
              <ToggleGroupItem value="video">Videos</ToggleGroupItem>
              <ToggleGroupItem value="channel">Channels</ToggleGroupItem>
              <ToggleGroupItem value="both">Both</ToggleGroupItem>
            </ToggleGroup>
          </Field>
          {recencyField}
        </FieldGroup>
      ) : value.type === 'image' ? (
        <ImageUploadField
          file={value.image}
          onFileSelected={(image) => {
            onChange({...value, image});
          }}
        />
      ) : (
        <FieldGroup className="gap-3">
          <Field>
            <FieldLabel htmlFor="investigation-text-value">
              {TEXT_TAB_CONFIG[value.type].fieldLabel}
            </FieldLabel>
            <Input
              autoFocus
              id="investigation-text-value"
              placeholder={TEXT_TAB_CONFIG[value.type].placeholder}
              value={value.textValue}
              onChange={(e) => {
                onChange({...value, textValue: e.target.value});
              }}
              onKeyDown={handleEnter}
            />
          </Field>
          {value.type === 'pinterest_profile' ? (
            <FieldGroup className="gap-1">
              <CollectOption
                label="Also collect this profile's followers"
                checked={value.collectFollowers}
                onChange={(checked) => {
                  // Boards can't run alongside followers (see the boards option below), and
                  // unticking followers takes its followers-of-followers refinement with it.
                  setProfileCollect({
                    collectFollowers: checked,
                    collectFollowersOfFollowers: false,
                    ...(checked ? {collectBoards: false} : {})
                  });
                }}
              />
              {value.collectFollowers ? (
                <CollectOption
                  nested
                  label="And their followers' followers"
                  checked={value.collectFollowersOfFollowers}
                  onChange={(checked) => {
                    setProfileCollect({
                      collectFollowers: true,
                      collectFollowersOfFollowers: checked
                    });
                  }}
                />
              ) : null}
              <CollectOption
                label="Also collect this profile's pins"
                checked={value.collectPins}
                onChange={(checked) => {
                  // Boards already collect every pin, so the two are alternatives.
                  setProfileCollect({
                    collectPins: checked,
                    ...(checked ? {collectBoards: false} : {})
                  });
                }}
              />
              <CollectOption
                label="Also collect pins board by board (not with followers or pins)"
                checked={value.collectBoards}
                onChange={(checked) => {
                  // The crawl's page limit multiplies per board, so it can't share a run with a
                  // follower crawl; and board-by-board already includes the pins.
                  setProfileCollect({
                    collectBoards: checked,
                    ...(checked
                      ? {
                          collectFollowers: false,
                          collectFollowersOfFollowers: false,
                          collectPins: false
                        }
                      : {})
                  });
                }}
              />
            </FieldGroup>
          ) : null}
          {value.type === 'google' ? recencyField : null}
          {value.type === 'google' ? (
            <Field>
              <FieldLabel>
                Search from <span className="text-muted-foreground font-normal">(optional)</span>
              </FieldLabel>
              <CountrySelect
                value={value.country}
                onChange={(country) => {
                  onChange({...value, country});
                }}
              />
              <FieldDescription>
                Searches as someone in this country would. Unset searches from anywhere.
              </FieldDescription>
            </Field>
          ) : null}
          {value.type === 'google' ? (
            <FieldGroup className="gap-2">
              <FieldLabel>Engines</FieldLabel>
              <CollectOption checked disabled label="Google" />
              {EXTRA_ENGINES.map((engine) => (
                <CollectOption
                  key={engine.value}
                  label={engine.label}
                  checked={value.extraEngines.includes(engine.value)}
                  onChange={(checked) => {
                    onChange({
                      ...value,
                      extraEngines: checked
                        ? [...value.extraEngines, engine.value]
                        : value.extraEngines.filter((e) => e !== engine.value)
                    });
                  }}
                />
              ))}
              <FieldDescription>
                Other engines find {PLATFORM_ID === 'pinterest' ? 'pins' : 'videos'} Google misses.
                Bing and Yandex are slow, adding up to 2 minutes.
              </FieldDescription>
            </FieldGroup>
          ) : null}
          {value.type === 'pinterest_search' ? (
            <Field orientation="horizontal">
              <FieldLabel>Results</FieldLabel>
              <ToggleGroup
                variant="outline"
                spacing={0}
                size="sm"
                aria-label="Results"
                value={[value.searchKind]}
                onValueChange={(next) => {
                  const searchKind = pick(next, ['profile', 'pin'] as const);
                  if (searchKind) {
                    onChange({...value, searchKind});
                  }
                }}>
                <ToggleGroupItem value="profile">Profiles</ToggleGroupItem>
                <ToggleGroupItem value="pin">Pins</ToggleGroupItem>
              </ToggleGroup>
            </Field>
          ) : null}
        </FieldGroup>
      )}
      <Field>
        <FieldLabel htmlFor="investigation-intent">
          Intent <span className="text-muted-foreground font-normal">(optional)</span>
        </FieldLabel>
        <Textarea
          id="investigation-intent"
          placeholder="Why are you running this investigation? e.g. monitoring election-related content ahead of the vote"
          value={value.intent}
          rows={3}
          className="resize-none"
          onChange={(e) => {
            onChange({...value, intent: e.target.value});
          }}
        />
      </Field>
      <Field>
        <FieldLabel>
          Language <span className="text-muted-foreground font-normal">(optional)</span>
        </FieldLabel>
        <LanguageSelect
          value={value.language}
          onChange={(language) => {
            onChange({...value, language});
          }}
        />
        <FieldDescription>
          {value.type === 'google'
            ? 'Labels the investigation, and sets the language the search engines use.'
            : 'Labels the investigation so it can be found and reused by language. Does not change what is collected.'}
        </FieldDescription>
      </Field>
      <TargetPolicyViolationsField
        value={value.targetPolicyViolations}
        onChange={(targetPolicyViolations) => {
          onChange({...value, targetPolicyViolations});
        }}
      />
    </>
  );
}

/** The policy violations an investigation's items are triaged into. Required: at least one. */
export function TargetPolicyViolationsField({
  value,
  onChange
}: {
  readonly value: string[];
  readonly onChange: (next: string[]) => void;
}) {
  const {data: classifications} = useClassificationConfig();
  const verticals = (classifications ?? []).filter(
    (c) => isPolicyViolationType(c.type) && isVisibleForFacet(c)
  );
  return (
    <Field>
      <FieldLabel>Target policy violations</FieldLabel>
      <ToggleGroup
        multiple
        variant="outline"
        size="sm"
        spacing={1}
        aria-label="Target policy violations"
        className="flex-wrap"
        value={value}
        onValueChange={(next) => {
          onChange(next.filter((name): name is string => typeof name === 'string'));
        }}>
        {verticals.map((v) => (
          <ToggleGroupItem key={v.id} value={v.name}>
            {value.includes(v.name) ? <Check /> : null}
            {v.displayName}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      <FieldDescription>
        {value.length === 0
          ? 'Required — select at least one policy violation to classify this investigation against.'
          : `Items will be triaged into ${value.length} policy violation queue${value.length === 1 ? '' : 's'}.`}
      </FieldDescription>
    </Field>
  );
}

function CollectOption({
  label,
  checked,
  disabled = false,
  nested = false,
  onChange
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly disabled?: boolean;
  /** An option that only applies on top of the one above it, so it is indented beneath it. */
  readonly nested?: boolean;
  /** Left out for an option that can't be changed. */
  readonly onChange?: (checked: boolean) => void;
}) {
  const id = useId();

  return (
    <Field orientation="horizontal" className={nested ? 'ml-6' : undefined}>
      <Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
      <FieldLabel htmlFor={id} className="font-normal">
        {label}
      </FieldLabel>
    </Field>
  );
}
